import { createFileRoute } from '@tanstack/react-router';
import { format, isAfter, isBefore, isSameDay } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UsersSelectorWithRoles } from '@/components/users/UsersSelectorWithRoles';
import { User } from '@/features/course-progress/api/types';
import { QuickSightDashboard } from '@/features/quicksight/components/QuickSightDashboard';
import { QuickSightEmbedError } from '@/features/quicksight/components/QuickSightEmbedError';
import { useQuickSightEmbedUrl } from '@/features/quicksight/hooks/useQuickSightEmbedUrl';
import { AppPageContent } from '@/features/shared/components/AppPageContent';
import { AppPageHeader } from '@/features/shared/components/AppPageHeader';
import { useOneRosterAuth } from '@/hooks/useOneRosterAuth';
import { UserRoles } from '@/lib/constants/auth.constants.ts';
import { SESSION_PERIODS_2025_2026 } from '@/lib/constants/report-dates';
import { getLocalTimezoneOffset } from '@/lib/datetime';
import { appendHashParams, resolveDashboardId } from '@/lib/quicksight';

export const Route = createFileRoute('/_app/app/my-learning-report')({
  component: RouteComponent,
  ssr: false,
});

function RouteComponent() {
  const { user, children } = useOneRosterAuth();
  const [selectedStudent, setSelectedStudent] = useState<
    User | undefined | null
  >(children[0] || user);

  const dashboardId = resolveDashboardId('myLearning');
  const {
    data,
    isLoading: isEmbedLoading,
    isError: isEmbedError,
  } = useQuickSightEmbedUrl(dashboardId);

  const sessionOptions = useMemo(() => {
    const today = new Date();

    const sessions = SESSION_PERIODS_2025_2026;

    const currentIdx = sessions.findIndex(
      (s) => !isBefore(today, s.start) && !isAfter(today, s.end)
    );

    const options: { value: string; label: string; start: Date; end: Date }[] =
      [];

    if (currentIdx !== -1) {
      const currentSession = sessions[currentIdx]!;
      options.push({
        value: 'current',
        label: 'Current Session to Date',
        start: currentSession.start,
        end: today,
      });
    }

    const completedSessions = sessions.filter((s) => isBefore(s.end, today));
    completedSessions.sort((a, b) => b.end.getTime() - a.end.getTime());

    completedSessions.forEach((session, index) => {
      const label =
        index === 0 ? 'Last Complete Session' : `${index + 1} Sessions Ago`;
      options.push({
        value: `${index + 1}`,
        label,
        start: session.start,
        end: session.end,
      });
    });

    const limited = options.slice(0, currentIdx === -1 ? 5 : 1 + 5);

    if (limited.length === 0) {
      const first = sessions[0]!;
      limited.push({
        value: '1',
        label: `Next session - (${format(first.start, 'MMM dd')} - ${format(first.end, 'MMM dd')})`,
        start: first.start,
        end: first.end,
      });
    }

    return limited;
  }, []);

  // Calculate min and max dates from sessionOptions
  const { minDate, maxDate } = useMemo(() => {
    const allSessions = SESSION_PERIODS_2025_2026;
    if (allSessions.length === 0) {
      return { minDate: undefined, maxDate: undefined };
    }

    const min = allSessions.reduce(
      (earliest, session) =>
        session.start < earliest ? session.start : earliest,
      allSessions[0]!.start
    );

    const max = allSessions.reduce(
      (latest, session) => (session.end > latest ? session.end : latest),
      allSessions[0]!.end
    );

    return { minDate: min, maxDate: max };
  }, []);

  const [selectedDateRange, setSelectedDateRange] = useState<
    DateRange | undefined
  >(() => {
    const defaultOption = sessionOptions[0];
    return defaultOption
      ? {
          from: defaultOption.start,
          to: defaultOption.end,
        }
      : undefined;
  });

  const [selectedOptionValue, setSelectedOptionValue] = useState(
    () => sessionOptions[0]!.value
  );

  // Check if current date range matches any session (comparing dates only, ignoring time)
  const dateRangeMatchesSession = useMemo(() => {
    if (!selectedDateRange?.from || !selectedDateRange?.to) return false;

    const matchingSession = sessionOptions.findIndex(
      (option) =>
        isSameDay(selectedDateRange.from!, option.start) &&
        isSameDay(selectedDateRange.to!, option.end)
    );

    if (matchingSession === -1) {
      return false;
    }

    setSelectedOptionValue(sessionOptions[matchingSession]!.value);

    return true;
  }, [selectedDateRange, sessionOptions]);

  const showDateRangeAlert =
    !dateRangeMatchesSession &&
    selectedDateRange?.from &&
    selectedDateRange?.to;

  const quickSightSrc = useMemo(() => {
    if (!data?.embedUrl || !selectedDateRange?.from || !selectedDateRange?.to) {
      return '';
    }

    const { offsetMinutes, offsetHours, offsetHourStr } =
      getLocalTimezoneOffset();

    const startDate = new Date(
      selectedDateRange.from.getTime() - offsetMinutes * 60 * 1000
    );
    const endDate = new Date(
      selectedDateRange.to.getTime() - offsetMinutes * 60 * 1000
    );

    const hashParams: Record<string, string | undefined> = {};

    if (selectedStudent?.email) {
      hashParams['p.email'] = selectedStudent.email;
    }
    if (selectedStudent?.sourcedId) {
      hashParams['p.studentSourcedId'] = selectedStudent.sourcedId;
    }
    hashParams['p.dateStart'] = format(selectedDateRange.from, 'yyyy-MM-dd');
    hashParams['p.dateEnd'] = format(
      selectedDateRange.to.setHours(23, 59, 59, 999),
      'yyyy-MM-dd'
    );
    hashParams['p.periodStart'] = format(startDate, 'yyyy-MM-dd HH:mm');
    hashParams['p.periodEnd'] = format(
      endDate.setHours(23, 59, 59, 999),
      'yyyy-MM-dd HH:mm'
    );
    hashParams['p.tzHourOffset'] = String(offsetHours);
    hashParams['p.offset'] = offsetHourStr;

    return appendHashParams(data.embedUrl, hashParams);
  }, [
    data?.embedUrl,
    selectedDateRange,
    selectedStudent?.email,
    selectedStudent?.sourcedId,
  ]);

  const handleDateRangeChange = (range?: DateRange) => {
    setSelectedDateRange(range);
    setSelectedOptionValue('');
  };

  const dashboardHeight = showDateRangeAlert
    ? 'calc(100vh - 240px)'
    : 'calc(100vh - 180px)';

  const sessionsFooter = useMemo(() => {
    return (
      <div className="flex flex-col justify-start items-start mx-2 mt-2 mb-2 px-4 py-2 pb-2 bg-gray-100 rounded-md">
        <h3 className="text-sm font-medium mb-1">Sessions:</h3>
        {sessionOptions.map((option) => (
          <p key={option.value} className="text-xs text-muted-foreground">
            {option.label} - {format(option.start, 'LLL dd, y')} -{' '}
            {format(option.end, 'LLL dd, y')}
          </p>
        ))}
      </div>
    );
  }, [sessionOptions]);

  return (
    <AppPageContent>
      <AppPageHeader
        title="My Learning Report"
        breadcrumbItems={[
          {
            label: 'App',
            link: { to: '/app' },
          },
          {
            label: 'My Learning Report',
            link: { to: '/app/my-learning-report' },
          },
        ]}
      />
      <div className="p-4 w-full max-w-full">
        <div className="flex justify-start gap-4">
          <StudentSelectWrapper
            selectedStudent={selectedStudent}
            setSelectedStudent={setSelectedStudent}
          />
          <div className="flex items-center gap-2 px-4 py-2">
            <span className="text-sm text-muted-foreground">
              Selected session:
            </span>
            <Select
              value={selectedOptionValue}
              onValueChange={(value) => {
                setSelectedOptionValue(value);
                const option = sessionOptions.find((o) => o.value === value);
                if (option) {
                  setSelectedDateRange({
                    from: option.start,
                    to: option.end,
                  });
                }
              }}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Select Session">
                  {selectedOptionValue
                    ? sessionOptions.find(
                        (o) => o.value === selectedOptionValue
                      )?.label
                    : 'Select Session'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {sessionOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="cursor-pointer"
                  >
                    <div>
                      {option.label}
                      <br />
                      <span className="text-xs text-muted-foreground">
                        {format(option.start, 'LLL dd, y')} -{' '}
                        {format(option.end, 'LLL dd, y')}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 px-4 py-2">
            <span className="text-sm text-muted-foreground">
              Selected session:
            </span>
            <DateRangePicker
              dateRange={selectedDateRange}
              onDateRangeChange={handleDateRangeChange}
              minDate={minDate}
              maxDate={maxDate}
              className="w-[220px]"
              buttonClassName="w-full"
              placeholder="Select date range"
              popoverFooter={sessionsFooter}
            />
          </div>
        </div>

        {isEmbedError && (
          <div className="px-4 mt-4">
            <QuickSightEmbedError />
          </div>
        )}

        {showDateRangeAlert && (
          <div className="px-4 mt-4">
            <Alert className="border-orange-200 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                The selected date range does not match any predefined session
                period. Reports will be generated for the custom date range.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>
      <div className="px-4 w-full max-w-full">
        <QuickSightDashboard
          src={quickSightSrc}
          isEmbedLoading={isEmbedLoading}
          height={dashboardHeight}
        />
      </div>
    </AppPageContent>
  );
}

function StudentSelectWrapper({
  selectedStudent,
  setSelectedStudent,
}: {
  selectedStudent: User | undefined | null;
  setSelectedStudent: (student: User | undefined | null) => void;
}) {
  const { isAdmin, isAide, isParent, isSysAdmin } = useOneRosterAuth();

  if (isAdmin || isAide || isParent || isSysAdmin) {
    return (
      <div className="flex items-center gap-2 px-4 py-2">
        <span className="text-sm text-muted-foreground">Selected student:</span>
        <UsersSelectorWithRoles
          selectedUserId={selectedStudent?.sourcedId}
          onUserSelect={setSelectedStudent}
          roles={[UserRoles.STUDENT]}
        />
      </div>
    );
  }
  return null;
}
